import React, { useState, useMemo } from 'react';
import { PlayerPositionState, CourtSurface, ShotRecommendation, DoublesFormation } from '../core/types';
import { calculateShotRecommendations, getCourtZone } from '../core/tactics';
import { TennisCourtCanvas } from '../components/Court/TennisCourtCanvas';
import { tennisAudio } from '../core/audio';
import { useI18n } from '../core/i18n';
import {
  Compass,
  Sparkles,
  Sliders,
  Shield,
  Zap,
  Target,
  Users,
  User,
  Layers,
  ChevronRight,
  Info,
} from 'lucide-react';

interface CourtArenaViewProps {
  surface: CourtSurface;
  onOpenCustomizer: () => void;
}

export const CourtArenaView: React.FC<CourtArenaViewProps> = ({ surface, onOpenCustomizer }) => {
  const { t } = useI18n();

  // Court positioning state
  const [courtState, setCourtState] = useState<PlayerPositionState>({
    playerA: { x: 70, y: 88 },
    partnerA: { x: 30, y: 56 },
    opponent1: { x: 30, y: 18 },
    opponent2: { x: 70, y: 44 },
    ball: { x: 72, y: 84 },
    incomingDirection: 'crosscourt',
    ballHeight: 'waist',
    gameMode: 'singles',
    formation: 'standard',
  });

  // Calculate top tactical shot options
  const recommendations = useMemo(() => {
    return calculateShotRecommendations(courtState);
  }, [courtState]);

  const [selectedShot, setSelectedShot] = useState<ShotRecommendation | null>(() => recommendations[0] || null);

  // Sync selected shot if recommendations change
  React.useEffect(() => {
    if (recommendations.length > 0) {
      setSelectedShot(recommendations[0]);
    }
  }, [courtState.ball.x, courtState.ball.y, courtState.gameMode]);

  const currentZone = getCourtZone(courtState.ball);

  const zoneConfig = {
    defensive_red: {
      label: '防守红区 (Red Defensive Zone - 底线深处)',
      color: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
      guidance: '严防死守，以高弧度深球重置相持节奏，绝不可在退守中盲目拼死直线！',
    },
    neutral_yellow: {
      label: '中立黄区 (Yellow Neutral Zone - 底线附近)',
      color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
      guidance: '扎实斜线相持，压制对手反手深角，等待对手回球出浅，切勿急躁！',
    },
    offensive_green: {
      label: '进攻绿区 (Green Attack Zone - 踏入场内)',
      color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
      guidance: '果断进攻！重心迎前，可根据空档选择变直线抽杀、追身或放短球！',
    },
  };

  const handleModeChange = (mode: 'singles' | 'doubles') => {
    setCourtState((prev) => ({
      ...prev,
      gameMode: mode,
    }));
    tennisAudio.playHit('flat');
  };

  const handleFormationChange = (formation: DoublesFormation) => {
    setCourtState((prev) => {
      let partnerA = { x: 30, y: 56 };
      let opp2 = { x: 70, y: 44 };

      if (formation === 'australian') {
        partnerA = { x: 65, y: 56 }; // Same side
      } else if (formation === 'i_formation') {
        partnerA = { x: 50, y: 54 }; // Crouched at center T
      } else if (formation === 'both_at_net') {
        partnerA = { x: 35, y: 55 };
        prev.playerA = { x: 65, y: 58 };
      }

      return {
        ...prev,
        formation,
        partnerA,
        opponent2: opp2,
      };
    });
    tennisAudio.playHit('topspin');
  };

  return (
    <div className="w-full h-full flex flex-col lg:flex-row gap-4 p-2 sm:p-4 max-w-7xl mx-auto overflow-y-auto">
      {/* Left Column: Interactive Court Canvas */}
      <div className="flex-1 flex flex-col items-center bg-slate-900/60 p-3 sm:p-4 rounded-2xl border border-slate-800 shadow-xl">
        {/* Top Controls Bar */}
        <div className="w-full flex flex-wrap items-center justify-between gap-2 mb-3">
          {/* Singles / Doubles Toggle */}
          <div className="flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-xs">
            <button
              onClick={() => handleModeChange('singles')}
              className={`flex items-center space-x-1.5 px-3 py-1 rounded-lg font-bold transition-all ${
                courtState.gameMode === 'singles'
                  ? 'bg-lime-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              <span>{t.singlesMode}</span>
            </button>
            <button
              onClick={() => handleModeChange('doubles')}
              className={`flex items-center space-x-1.5 px-3 py-1 rounded-lg font-bold transition-all ${
                courtState.gameMode === 'doubles'
                  ? 'bg-lime-500 text-slate-950 shadow-sm'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Users className="w-3.5 h-3.5" />
              <span>{t.doublesMode}</span>
            </button>
          </div>

          {/* Doubles Formation Presets */}
          {courtState.gameMode === 'doubles' && (
            <div className="flex items-center gap-1 text-[11px]">
              <span className="text-slate-400">阵型:</span>
              <button
                onClick={() => handleFormationChange('standard')}
                className={`px-2 py-0.5 rounded ${
                  courtState.formation === 'standard' ? 'bg-slate-800 text-lime-400 font-bold' : 'text-slate-400'
                }`}
              >
                标准传统
              </button>
              <button
                onClick={() => handleFormationChange('i_formation')}
                className={`px-2 py-0.5 rounded ${
                  courtState.formation === 'i_formation' ? 'bg-slate-800 text-lime-400 font-bold' : 'text-slate-400'
                }`}
              >
                I阵型暗号
              </button>
              <button
                onClick={() => handleFormationChange('australian')}
                className={`px-2 py-0.5 rounded ${
                  courtState.formation === 'australian' ? 'bg-slate-800 text-lime-400 font-bold' : 'text-slate-400'
                }`}
              >
                澳式同侧
              </button>
            </div>
          )}

          {/* Court Surface Customizer Button */}
          <button
            onClick={onOpenCustomizer}
            className="flex items-center space-x-1 px-2.5 py-1 rounded-xl bg-slate-800/80 hover:bg-slate-750 text-xs text-slate-200 border border-slate-700/80 transition-colors"
          >
            <Sliders className="w-3.5 h-3.5 text-lime-400" />
            <span>{t.changeCourtTheme}</span>
          </button>
        </div>

        {/* Tennis Court Sandbox */}
        <TennisCourtCanvas
          surface={surface}
          state={courtState}
          onUpdateState={setCourtState}
          selectedShot={selectedShot}
          onSelectShot={setSelectedShot}
        />

        {/* Quick hint */}
        <div className="mt-3 text-[11px] text-slate-400 text-center flex items-center gap-1">
          <Info className="w-3.5 h-3.5 text-lime-400 shrink-0" />
          <span>{t.dragBallTip}</span>
        </div>
      </div>

      {/* Right Column: AI Coach Tactical Guidance & Shot Recommendations */}
      <div className="w-full lg:w-[420px] flex flex-col space-y-4">
        {/* Court Zone Assessment Header */}
        <div className={`p-3.5 rounded-2xl border ${zoneConfig[currentZone].color} backdrop-blur-md`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-black uppercase tracking-wider">
              {zoneConfig[currentZone].label}
            </span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-950/60 font-mono">
              X:{Math.round(courtState.ball.x)}% Y:{Math.round(courtState.ball.y)}%
            </span>
          </div>
          <p className="text-xs leading-relaxed text-slate-200">{zoneConfig[currentZone].guidance}</p>
        </div>

        {/* Tactical Shot Recommendations */}
        <div className="bg-slate-900/80 p-4 rounded-2xl border border-slate-800 shadow-xl flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-lime-400" />
              <h3 className="text-xs font-black uppercase tracking-wider text-white">
                {t.coachAdvice} · {t.shotRecommendation}
              </h3>
            </div>
            <span className="text-[10px] text-slate-400 font-mono bg-slate-950 px-2 py-0.5 rounded-md border border-slate-800">
              沃德洛法则推演
            </span>
          </div>

          {/* Spin Mode Filter Bar */}
          <div className="flex items-center gap-1.5 mb-3 bg-slate-950 p-1.5 rounded-xl border border-slate-800 text-xs">
            <span className="text-[10px] text-slate-400 font-bold ml-1">旋转模式:</span>
            {[
              { id: 'all', label: '全部' },
              { id: 'topspin', label: '🌀 强上旋' },
              { id: 'flat', label: '⚡ 穿透平击' },
              { id: 'slice', label: '🧊 贴地切削' },
            ].map((sp) => (
              <button
                key={sp.id}
                onClick={() => {
                  setCourtState((prev) => ({ ...prev, spinFilter: sp.id as any }));
                  tennisAudio.playHit(sp.id === 'slice' ? 'slice' : 'topspin');
                }}
                className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all ${
                  (courtState.spinFilter || 'all') === sp.id
                    ? 'bg-sky-500 text-slate-950 shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {sp.label}
              </button>
            ))}
          </div>

          {/* List of recommended shots */}
          <div className="space-y-2.5">
            {recommendations.map((rec, index) => {
              const isSelected = selectedShot?.id === rec.id;
              return (
                <div
                  key={rec.id}
                  onClick={() => {
                    setSelectedShot(rec);
                    tennisAudio.playHit(rec.strokeType.includes('slice') ? 'slice' : 'topspin');
                  }}
                  className={`p-3 rounded-xl border cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-slate-850 border-lime-400 shadow-md ring-1 ring-lime-400/40'
                      : 'bg-slate-950/70 border-slate-800 hover:border-slate-700 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                          index === 0
                            ? 'bg-lime-500 text-slate-950'
                            : 'bg-slate-800 text-slate-300'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="text-xs font-bold text-white">{rec.name}</span>
                    </div>

                    <div className="flex items-center space-x-1.5">
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                          rec.riskLevel === 'low'
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : rec.riskLevel === 'medium'
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                        }`}
                      >
                        胜率 {rec.successRate}%
                      </span>
                    </div>
                  </div>

                  {/* Tactical Principle Note */}
                  <p className="text-[11px] text-slate-300 leading-snug mb-2">{rec.tacticalPrinciple}</p>

                  {/* Shot Metrics: Speed, Clearance, RPM & Bounce */}
                  <div className="grid grid-cols-4 gap-1 text-[10px] bg-slate-900/90 p-2 rounded-lg border border-slate-800/80 text-center">
                    <div>
                      <span className="text-slate-500 block">建议转速</span>
                      <span className="font-mono font-bold text-sky-400">{rec.rpm} RPM</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">反弹高度</span>
                      <span className="font-mono font-bold text-amber-400">{rec.bounceHeightMeters}m</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">过网高度</span>
                      <span className="font-mono font-bold text-lime-400">{rec.netClearanceFeet}ft</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block">建议球速</span>
                      <span className="font-mono font-bold text-slate-200">{rec.speedMph}mph</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Magnus Aerodynamics & Drill Tip Deck */}
          {selectedShot && (
            <div className="mt-4 space-y-2">
              {/* Magnus Aerodynamics Card */}
              <div className="p-3 bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 rounded-xl border border-sky-500/30 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-sky-400 font-bold uppercase tracking-wider flex items-center gap-1">
                    <span>🌀 马格努斯空气动力学 (Magnus Effect)</span>
                  </span>
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">
                    {selectedShot.rpm} RPM · {selectedShot.spinType.toUpperCase()}
                  </span>
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed">
                  {selectedShot.magnusEffectDesc}
                </p>
              </div>

              {/* Coach Drill Tip */}
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 text-xs">
                <div className="text-[10px] text-amber-400 font-bold uppercase tracking-wider mb-1">
                  🏋️ 欢乐特训要领 (Coach Drill Tip)
                </div>
                <p className="text-[11px] text-slate-400 leading-normal">{selectedShot.drillNote}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
